import { ContentRow } from "@dropbox/dash-component-library";
import { Button } from "@dropbox/dig-components/buttons";
import { Keycap } from "@dropbox/dig-components/keycap";
import { Title } from "@dropbox/dig-components/typography";
import { Atoms, Box } from "@dropbox/dig-foundations";
import { EnvCtx } from "@mirage/service-environment-context/global-env-ctx";
import { useOnboardingValues } from "@mirage/service-onboarding/hooks";
import useSettings from "@mirage/service-settings/useSettings";
import { settingsShortcutToFancyShortcut } from "@mirage/shared/hotkeys";
import i18n from "@mirage/translations";
import classnames from "classnames";
import * as React from "react";
import { SearchIconImage } from "./assets";
import styles from "./LauncherRowTip.module.css";

export const LauncherRowTip: React.FC<{
  onDismiss?: () => void;
  marginTop?: Atoms["marginTop"];
  marginBottom?: Atoms["marginBottom"];
  animate?: boolean;
  animateDelayMs?: number;
}> = ({
  onDismiss,
  marginTop,
  marginBottom,
  animate = false,
  animateDelayMs = 0,
}) => {
  const { settings } = useSettings(["appShortcut"]);
  const { getOnboardingValue, setOnboardingValue } = useOnboardingValues();
  const isDarwin = EnvCtx.platform === "darwin";
  const launcherShortcut = settingsShortcutToFancyShortcut(
    isDarwin,
    settings?.appShortcut ?? "",
  );

  React.useEffect(() => {
    const updateSeenCount = async () => {
      const seenCount = await getOnboardingValue("seenLauncherSearchTip");
      setOnboardingValue("seenLauncherSearchTip", seenCount + 1);
    };
    updateSeenCount();
  }, []);

  const handleDismiss = () => {
    onDismiss?.();
    setOnboardingValue("dismissedLauncherSearchTip", true);
  };

  return (
    <Box marginTop={marginTop ?? "0"} marginBottom={marginBottom ?? "0"}>
      <ContentRow
        variant="borderless"
        className={classnames(styles.tipContainer, {
          [styles.animateIn]: animate,
        })}
        style={{ animationDelay: `${animateDelayMs}ms` }}
        paddingSize="medium"
        withStartAccessory={
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            borderRadius="Small"
            className={styles.searchIconContainer}
          >
            <img
              src={SearchIconImage}
              alt={i18n.t("search_icon_alt")}
              className={styles.searchIcon}
            />
          </Box>
        }
        withLabel={
          <Box display="flex" alignItems="center">
            <Title size="small" weightVariant="emphasized">
              {i18n.t("tip_colon")}&nbsp;&nbsp;
            </Title>
            <Keycap.Group
              size="small"
              variant="opacity"
              keycaps={launcherShortcut.map((key) => ({
                symbol: key,
                withAccessibleLabel: key,
              }))}
              withAccessibleAdjacentLabel={i18n.t("conjunction_and")}
            />
            <Title size="small" weightVariant="emphasized">
              &nbsp;{i18n.t("tip_dual_mode_launcher_blurb")}
            </Title>
          </Box>
        }
        withActions={
          <Button variant="borderless" onClick={handleDismiss}>
            Dismiss
          </Button>
        }
      />
    </Box>
  );
};
